import { useCallback, useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Info, Undo2, X, XCircle } from 'lucide-react';
import type { CheckInTone } from './feedback';

/** How long a result stays on screen, and how long it may be undone. */
export const UNDO_WINDOW_MS = 5000;

export interface CheckInToastState {
  /** Distinguishes one result from the next; each keeps its own clock. */
  id: number;
  tone: CheckInTone;
  title: string;
  detail?: string;
  onUndo?: () => void;
}

const TONES: Record<CheckInTone, { box: string; icon: typeof CheckCircle2 }> = {
  success: { box: 'bg-green-600 text-white', icon: CheckCircle2 },
  warning: { box: 'bg-amber-500 text-white', icon: AlertTriangle },
  error: { box: 'bg-red-600 text-white', icon: XCircle },
  info: { box: 'bg-gray-800 text-gray-50', icon: Info },
};

function ToastItem({ toast, onDismiss }: { toast: CheckInToastState; onDismiss: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, UNDO_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [onDismiss]);

  const { box, icon: Icon } = TONES[toast.tone];
  return (
    <div
      role="status"
      aria-live="polite"
      data-tone={toast.tone}
      className={`pointer-events-auto w-full max-w-md rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 ${box}`}
    >
      <Icon className="w-6 h-6 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold truncate">{toast.title}</div>
        {toast.detail && <div className="text-sm opacity-90">{toast.detail}</div>}
      </div>
      {toast.onUndo && (
        <button
          type="button"
          onClick={() => {
            toast.onUndo?.();
            onDismiss();
          }}
          className="inline-flex items-center gap-1 rounded-lg bg-white/20 px-3 py-2 text-sm font-semibold hover:bg-white/30"
        >
          <Undo2 className="w-4 h-4" /> Undo
        </button>
      )}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="p-1 rounded opacity-80 hover:opacity-100"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * The results of check-ins as banners along the bottom of the screen — never
 * a dialog to press OK on, so the next guest can be scanned straight away.
 * Each leaves after five seconds and offers its Undo for that long, however
 * many guests are scanned meanwhile; the newest sits at the bottom.
 */
export function CheckInToast({
  toasts,
  onDismiss,
}: {
  toasts: CheckInToastState[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed inset-x-0 bottom-4 z-40 flex flex-col items-center gap-2 px-4 pointer-events-none theme-light">
      {toasts.map((toast) => (
        <ToastItemBound key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

/** Binds the dismissal to one toast, stable across renders so its timer never restarts. */
function ToastItemBound({
  toast,
  onDismiss,
}: {
  toast: CheckInToastState;
  onDismiss: (id: number) => void;
}) {
  const { id } = toast;
  const dismiss = useCallback(() => onDismiss(id), [onDismiss, id]);
  return <ToastItem toast={toast} onDismiss={dismiss} />;
}
